/**
 * The board capsule — one component for all three desktop boards
 * (INVENTORY-DESKTOP §3.4 / §3.5 / §3.6, §6.4).
 *
 * Tonight, Interests and GOTY paint the same thing: a 116px column with a 2/3
 * cover, a rAF-tilted frame, a sheen layer INSIDE that cover, and a mono
 * caption under it. They differ only in what goes in the caption, whether the
 * capsule is draggable, and whether the cover carries an overlay.
 *
 * WHY THE HOOK AND NOT `HoverTile`. `HoverTile` appends the sheen as the tilted
 * element's LAST child, which is right for a Discover tile and wrong here: the
 * prototype puts the sheen inside the inner cover box (lines 210 / 275 / 350),
 * under Tonight's "EVERYONE'S CALL" overlay and above nothing else. Putting it
 * last would paint it over the caption and under the overlay's gradient. D0
 * spells this out (§3.1: "use the hook, not HoverTile").
 *
 * WHY `useArt` LIVES HERE. It is one hook per tile, so it cannot run inside a
 * `.map` in the screen — the same reason the mobile screens each grew a
 * `Capsule` component. It also means the resolved cover is only known in here,
 * which is why `dragProps` takes a ghost THUNK: the drag ghost paints the same
 * background this capsule resolved, and it can only be read at drag time.
 *
 * The caller owns every size/border/opacity/transition value — those are the
 * §5.5 drag-feedback strings and they belong to the screen's drag state, not to
 * this component.
 */

import type { CSSProperties, ReactNode } from 'react';

import { CoverArt } from '../../components/CoverArt.tsx';
import { useArt } from '../../components/useArt.ts';
import type { ArtCand, GameId } from '../../lib/types.ts';
import { useHoverTilt } from '../useHoverTilt.ts';
import type { TiltId } from '../useHoverTilt.ts';
import type { BoardDnd } from '../useHtml5BoardDnd.ts';

export interface BoardCapsuleProps {
  id: GameId;
  /**
   * Tilt identity — must be unique among MOUNTED tilted elements. Defaults to
   * `id`, which is right on Tonight and Interests; GOTY needs a prefix on the
   * TO-DO cover because the same game can be mounted twice (§3.1).
   */
  tiltId?: TiltId;
  nameUp: string;
  fall: string;
  ini: string;
  coverCands: readonly ArtCand[];
  /** cover initials px — every board capsule call site is 13 */
  fs?: number;
  /** `filter` on the capsule: `saturate(.4)` on Tonight's ✕ row and Interests' ▼ */
  filt?: string;
  /** width / margin-left / border / opacity / transition / cursor / overflow */
  style?: CSSProperties;
  /**
   * The drag shift — a `translate(...)` that carries this capsule to the slot
   * it occupies while the move is previewed (`useHtml5BoardDnd#capVis`).
   *
   * A prop rather than part of `style` because it has to be COMPOSED with the
   * tilt's transform below, not overwrite it: both want the same property, and
   * the tilt's is applied last so it would win. Order matters — the shift is a
   * layout-space translation and must be the OUTER transform, or the tilt's
   * `scale`/`perspective` would scale the distance travelled too.
   */
  shift?: string;
  onClick?: () => void;
  /** omit on Tonight — the shared board is read-only and not a drag source (§5.1) */
  dnd?: BoardDnd | undefined;
  /** painted inside the cover box, above the sheen (Tonight's vote overlay) */
  coverOverlay?: ReactNode;
  /** the caption block under the cover */
  children?: ReactNode;
}

export function BoardCapsule({
  id,
  tiltId,
  nameUp,
  fall,
  ini,
  coverCands,
  fs = 13,
  filt = 'none',
  style,
  shift,
  onClick,
  dnd,
  coverOverlay,
  children,
}: BoardCapsuleProps): JSX.Element {
  const art = useArt(id + 'p', coverCands);
  const tilt = useHoverTilt(tiltId ?? id);

  const drag = dnd
    ? dnd.dragProps(id, () => ({
        id,
        nameUp,
        coverBg: art.hasCover ? art.cover : '',
        fall,
      }))
    : null;

  return (
    <div
      {...(drag ?? {})}
      ref={tilt.ref}
      {...tilt.handlers}
      onClick={onClick}
      style={{
        position: 'relative',
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--gt-tile)',
        filter: filt,
        ...style,
        // transform / box-shadow / z-index are the tilt's, always last
        ...tilt.rest,
        // ...except when a drag shift is live. Composed, not replaced: the
        // shift is the outer (layout-space) translation, the tilt's transform
        // the inner one, so a tilted capsule still travels the right distance.
        ...(shift ? { transform: shift + ' ' + String(tilt.rest.transform ?? '') } : {}),
      }}
    >
      <div style={{ position: 'relative', aspectRatio: '2/3', background: fall, overflow: 'hidden' }}>
        <CoverArt
          style={{ position: 'absolute', inset: 0 }}
          fs={fs}
          ini={ini}
          hasCover={art.hasCover}
          cover={art.cover}
        />
        <div ref={tilt.sheenRef} style={tilt.restSheen} />
        {coverOverlay}
      </div>
      {children}
    </div>
  );
}

export default BoardCapsule;
